import type { FC, PropsWithChildren } from 'react';
import { notification, Row, Spin } from 'antd';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';
import { isNil } from 'ramda';
import { sessionState } from 'store';
import { useServices } from 'containers/services/useServices';
import axios from 'axios';
import { isStrapiApiErrorResponse } from 'utils/isStrapiApiErrorResponse';
import { Icon } from '@/components/icon';

const useRenewToken = () => {
  const [{ isAuthenticated: isRenewed, jwt }, setSession] = useRecoilState(sessionState);
  const { adminApiClient } = useServices();

  const { isFetching } = useQuery(['renew-token', jwt], async ({ signal }) => await adminApiClient.renewToken(signal), {
    enabled: !(isNil(jwt) || isRenewed),
    retry: false,
    onError: e => {
      console.error(e);
      setSession({ isAuthenticated: false });
      notification.error({
        message: 'Failed to renew token',
        description:
          axios.isAxiosError(e) && e.response?.status === 400 && isStrapiApiErrorResponse(e.response?.data)
            ? e.response.data.error.message
            : undefined,
      });
    },
    onSuccess: ({ jwt: token }) => {
      setSession(prevSession => ({ ...prevSession, jwt: token, isAuthenticated: true }));
    },
  });

  return {
    isRenewing: isFetching,
  };
};

export const TokenRenewal: FC<PropsWithChildren<object>> = ({ children }) => {
  const { isRenewing } = useRenewToken();

  return isRenewing ? (
    <Row justify="center">
      <Spin indicator={<Icon iconString="spinner" spin />} size="large" tip="Renewing token ..." />
    </Row>
  ) : (
    <>{children}</>
  );
};
